import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { GET_ORDER_STATS, GET_PRODUCT_SALES_STATS } from '../graphql/queries'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { auth } from '../lib/auth'
import type { OrderStats } from '../types/order'
import YesterdayHandoverWidget from './dashboard/YesterdayHandoverWidget'
import AnnouncementsWidget from './dashboard/AnnouncementsWidget'
import PlannedOrdersWidget from './dashboard/PlannedOrdersWidget'
import { formatEuros, formatDuration } from '../lib/format'
import { MarginWaterfall } from '../components/financials/MarginWaterfall'
import { buildSourceColumns, SourceBreakdown } from '../components/financials/SourceBreakdown'
import { FulfilmentBreakdown, FULFILMENT_TYPES } from '../components/financials/FulfilmentBreakdown'

// Speed benchmarks in seconds. The bar turns red above them. Delivery: confirmed
// (kitchen release for a pre-order) → the driver taps Delivered; 45 min is a
// placeholder until the owner names a figure (todo §0e).
const PREP_TARGET_SECONDS = 15 * 60
const PICKUP_TARGET_SECONDS = 25 * 60
const DELIVERY_TARGET_SECONDS = 45 * 60

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">{children}</h2>
)

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center h-32 gap-2 text-sm text-gray-400">
    <svg className="w-7 h-7 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
    {message}
  </div>
)

const SpeedBar = ({ label, sub, value, benchmark, count, countLabel = 'orders', extra }: {
  label: string
  sub: string
  /** Average seconds; null/undefined when the sample is empty. */
  value: number | null | undefined
  benchmark: number
  count: number
  countLabel?: string
  /** Optional line under the bar (e.g. on-the-road and longest delivery). */
  extra?: React.ReactNode
}) => {
  const avg = value ?? 0
  const pct = benchmark > 0 ? Math.min((avg / benchmark) * 100, 100) : 0
  const isOver = count > 0 && avg > benchmark
  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">{label}</p>
          <p className="text-xs text-gray-400">{sub}</p>
        </div>
        <div className="text-right flex-shrink-0 ml-4">
          <p className={`text-2xl font-bold leading-none ${isOver ? 'text-red-500' : 'text-gray-900'}`}>
            {count > 0 ? formatDuration(avg) : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{count} {countLabel}</p>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isOver ? 'bg-red-400' : 'bg-emerald-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-gray-300">0</span>
        <span className="text-xs text-gray-400">
          Target: {formatDuration(benchmark)}
          {isOver && (
            <span className="ml-1 text-red-400 font-semibold">(+{formatDuration(avg - benchmark)})</span>
          )}
        </span>
      </div>
      {extra && <div className="mt-2 text-xs text-gray-500">{extra}</div>}
    </div>
  )
}

// Rank badge colours: 1=jasper, 2=plum, 3=butterscotch, 4+=gray
const RANK_COLORS = ['#CA4330', '#853F82', '#DC9C4E']
const getRankColor = (i: number) => RANK_COLORS[i] ?? '#9CA3AF'

// ─── Dashboard Page ──────────────────────────────────────────────────────────

type Period = 'day' | 'yesterday' | 'week'

const DashboardPage = () => {
  const [period, setPeriod] = useState<Period>('day')

  const { hasAnyPermission, hasPermission, canViewOrderFinancials } = usePermissions()
  const canSeeProductSales = hasPermission(PERMISSIONS.STATS_PRODUCT_SALES_VIEW)
  const canSeeHandover = hasPermission(PERMISSIONS.PRODUCTION_LOG)

  const yesterdayString = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d.toISOString().slice(0, 10)
  }, [])

  const can7Days = hasAnyPermission([
    PERMISSIONS.ORDER_LIST_7DAYS,
    PERMISSIONS.ORDER_LIST_7DAYS_PLANNED,
    PERMISSIONS.ORDER_LIST_ALL,
    PERMISSIONS.ORDER_DETAILS_7DAYS,
    PERMISSIONS.ORDER_DETAILS_ALL,
  ])

  const canSeeRevenue = can7Days
  const canFin = canViewOrderFinancials()
  // Financial users see the money table even without the 7-day history permission.
  const showSourceTable = canSeeRevenue || canFin

  // Snap back to today if user doesn't have 7-day permission
  useEffect(() => {
    if (!can7Days && (period === 'yesterday' || period === 'week')) setPeriod('day')
  }, [can7Days, period])

  // ── Date range ──────────────────────────────────────────────────────────────
  const { dateFrom, dateTo, periodLabel } = useMemo(() => {
    const now = new Date()
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
    if (period === 'day') {
      return {
        dateFrom: todayStart.toISOString(),
        dateTo: todayEnd.toISOString(),
        periodLabel: todayStart.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }),
      }
    }
    if (period === 'yesterday') {
      const yesterdayStart = new Date(todayStart)
      yesterdayStart.setDate(yesterdayStart.getDate() - 1)
      const yesterdayEnd = new Date(yesterdayStart.getFullYear(), yesterdayStart.getMonth(), yesterdayStart.getDate(), 23, 59, 59)
      return {
        dateFrom: yesterdayStart.toISOString(),
        dateTo: yesterdayEnd.toISOString(),
        periodLabel: yesterdayStart.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }),
      }
    }
    const weekStart = new Date(todayStart)
    weekStart.setDate(weekStart.getDate() - 6)
    return {
      dateFrom: weekStart.toISOString(),
      dateTo: todayEnd.toISOString(),
      periodLabel: `${weekStart.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} – ${todayStart.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`,
    }
  }, [period])

  // ── Filters for both queries ──────────────────────────────────────────────────
  // Neither source nor type is a filter any more: channels are the columns of the
  // Sales by Channel table, fulfilment types are the tiles below it. (The old
  // Dine In / Takeaway checkboxes had no Delivery option, so they silently
  // included delivery orders with both ticked and excluded them with one.)
  const queryFilters = { date_from: dateFrom, date_to: dateTo }

  // ── Queries ───────────────────────────────────────────────────────────────────
  const { data: statsData, loading: statsLoading, error: statsError } = useQuery(GET_ORDER_STATS, {
    variables: { filters: queryFilters },
    fetchPolicy: 'cache-and-network',
  })

  const { data: productData, loading: productLoading, error: productError } = useQuery(GET_PRODUCT_SALES_STATS, {
    variables: { filters: queryFilters },
    fetchPolicy: 'cache-and-network',
    skip: !canSeeProductSales,
  })

  const loading = statsLoading || (canSeeProductSales && productLoading)
  const error = statsError ?? (canSeeProductSales ? productError : undefined)

  // ── KPI data (direct from API) ────────────────────────────────────────────────
  const stats: OrderStats | undefined = statsData?.orderStats
  const orderCount: number = stats?.orderCount ?? 0
  const speed = stats?.speed
  const deliverySpeed = stats?.deliverySpeed

  // ── Per-channel columns (Counter / Kiosk / Webshop / platforms / Total) ────────
  const sourceData = useMemo(() => (stats ? buildSourceColumns(stats, canFin) : null), [stats, canFin])

  // ── Product data (direct from API) ────────────────────────────────────────────
  const productStats = productData?.productSalesStats

  const topProducts = useMemo(() => (productStats?.topProducts ?? []).map((p: any) => ({
    ...p,
    key: p.variantName === p.productName ? p.productName : `${p.productName} – ${p.variantName}`,
  })), [productStats])

  const topUpgrades: any[] = productStats?.topUpgrades ?? []
  const totalUpgradeCount: number = topUpgrades.reduce((s, u) => s + u.count, 0)
  const totalUpgradeRevCents: number = topUpgrades.reduce((s, u) => s + (u.revenueCents ?? 0), 0)
  const maxProductQty: number = topProducts[0]?.qtySold ?? 1
  const maxUpgradeCount: number = topUpgrades[0]?.count ?? 1
  const customizationGroups: any[] = productStats?.customizationFrequency ?? []

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-0.5 text-sm text-gray-500 capitalize">{periodLabel}</p>
          <p className="mt-1 text-sm text-gray-400">Welcome, {auth.getUser()?.name ?? 'there'}. Here's an overview of today.</p>
        </div>
        {loading && (
          <span className="flex items-center gap-2 text-xs text-gray-400 self-start sm:self-auto">
            <span className="w-2 h-2 rounded-full bg-gray-300 animate-pulse inline-block" />
            Refreshing…
          </span>
        )}
      </div>

      {/* ── Announcements (unread / needs-ack) ───────────────────────────── */}
      <AnnouncementsWidget />

      {/* ── Yesterday's handover ─────────────────────────────────────────── */}
      {canSeeHandover && <YesterdayHandoverWidget yesterday={yesterdayString} />}

      {/* ── Planned orders (pre-orders) due today ────────────────────────────
           Always "today" regardless of the period filter below — deliberately
           mounted above it. Self-gating; renders null when empty. */}
      <PlannedOrdersWidget />

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Period</span>
          <div className="flex rounded-lg overflow-hidden ring-1 ring-gray-200">
            {(['day', ...(can7Days ? ['yesterday', 'week'] : [])] as Period[]).map((p, i) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                  period === p ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${i > 0 ? 'border-l border-gray-200' : ''}`}
              >
                {p === 'day' ? 'Today' : p === 'yesterday' ? 'Yesterday' : 'Last 7 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Sales by channel (where the order was placed) ───────────────────── */}
      {showSourceTable ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <SectionTitle>Sales by Channel</SectionTitle>
            {canFin && (stats?.linesWithoutCogsCount ?? 0) > 0 && (
              <p className="text-xs text-amber-600 -mt-5">
                {stats?.linesWithoutCogsCount} items missing recipe — COGS incomplete, actual margin lower than shown
              </p>
            )}
          </div>
          {orderCount === 0 || !sourceData ? (
            <EmptyState message="No completed orders in this period" />
          ) : (
            <SourceBreakdown columns={sourceData.columns} total={sourceData.total} canFin={canFin} />
          )}
        </div>
      ) : (
        // Users without revenue permission still see order counts.
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Orders</p>
          <p className="text-3xl font-bold text-gray-900 leading-none">{orderCount}</p>
          <div className="flex gap-2 flex-wrap">
            {FULFILMENT_TYPES
              .map((t) => ({ label: t.label, n: stats?.byType?.find((r) => r.orderType === t.type)?.orderCount ?? 0 }))
              .filter((t) => t.n > 0)
              .map((t, i) => (
                <span key={t.label} className="text-xs text-gray-400">
                  {i > 0 && <span className="text-gray-300 mr-2">·</span>}
                  {t.label}: {t.n}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* ── Sales by fulfilment (how the order reached the customer) ────────── */}
      {showSourceTable && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <SectionTitle>Sales by Fulfilment</SectionTitle>
            <p className="text-xs text-gray-400 -mt-5">all channels together</p>
          </div>
          {orderCount === 0 || !stats ? (
            <EmptyState message="No completed orders in this period" />
          ) : (
            <FulfilmentBreakdown stats={stats} canFin={canFin} />
          )}
        </div>
      )}

      {/* ── Staff meals (comp — cost absorbed, excluded from everything above) ── */}
      {showSourceTable && (stats?.staffMealCount ?? 0) > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Staff Meals</p>
            <p className="text-xs text-gray-400 mt-0.5">Comps booked at €0 — excluded from sales, discounts &amp; product margin above.</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-gray-900 leading-none">
              {canFin ? formatEuros(stats?.staffMealCostCents ?? 0) : (stats?.staffMealCount ?? 0)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {canFin ? `cost absorbed · ${stats?.staffMealCount ?? 0} meals` : 'meals'}
            </p>
          </div>
        </div>
      )}

      {/* ── Money flow (total VAT-honest waterfall → product margin) ───────────── */}
      {canFin && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionTitle>Money Flow (total)</SectionTitle>
          {orderCount === 0 ? (
            <EmptyState message="No completed orders in this period" />
          ) : stats ? (
            <MarginWaterfall stats={stats} />
          ) : null}
        </div>
      )}

      {/* ── Order speed ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <SectionTitle>Order Speed</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-7">
          <SpeedBar
            label="Preparation Time"
            sub="All orders · confirmed → ready"
            value={speed?.avgPrepSeconds}
            benchmark={PREP_TARGET_SECONDS}
            count={speed?.sampleCount ?? 0}
          />
          <SpeedBar
            label="Pickup Wait"
            sub="Dine-in + takeaway · confirmed → handed over"
            value={speed?.avgThroughputSeconds}
            benchmark={PICKUP_TARGET_SECONDS}
            count={speed?.throughputSampleCount ?? 0}
          />
          {/* Own drivers only: platform couriers never report a delivered moment.
              The clock starts at confirmed (kitchen release for a pre-order) and
              stops when the driver taps Delivered. */}
          <SpeedBar
            label="Delivery Time"
            sub="Own drivers · confirmed → delivered"
            value={deliverySpeed?.avgDeliverySeconds}
            benchmark={DELIVERY_TARGET_SECONDS}
            count={deliverySpeed?.sampleCount ?? 0}
            countLabel="deliveries"
            extra={(deliverySpeed?.sampleCount ?? 0) > 0 && (
              <>
                {deliverySpeed?.avgOnRoadSeconds != null && (
                  <>On the road <span className="font-semibold text-gray-900">{formatDuration(deliverySpeed.avgOnRoadSeconds)}</span><span className="mx-1.5 text-gray-300">·</span></>
                )}
                Longest{' '}
                <span className={`font-semibold ${(deliverySpeed?.maxDeliverySeconds ?? 0) > DELIVERY_TARGET_SECONDS ? 'text-red-500' : 'text-gray-900'}`}>
                  {formatDuration(deliverySpeed?.maxDeliverySeconds ?? 0)}
                </span>
              </>
            )}
          />
        </div>
        {orderCount > 0 && (speed?.sampleCount ?? 0) === 0 && (
          <p className="mt-5 text-xs text-gray-400 text-center">Speed data appears once orders have timing recorded.</p>
        )}
      </div>

      {/* ── Top Selling Products ───────────────────────────────────────────── */}
      {canSeeProductSales && <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <SectionTitle>Top Selling Products</SectionTitle>
          {topProducts.length > 0 && (
            <span className="text-xs text-gray-400 -mt-5">
              {topProducts.reduce((s: number, p: any) => s + p.qtySold, 0)} items sold
            </span>
          )}
        </div>

        {orderCount === 0 ? (
          <EmptyState message="No completed orders in this period" />
        ) : topProducts.length === 0 ? (
          <EmptyState message="No product line data available" />
        ) : (
          <div className="space-y-3">
            {topProducts.map((product: any, i: number) => {
              const barPct = (product.qtySold / maxProductQty) * 100
              const rankColor = getRankColor(i)
              return (
                <div key={product.key} className="flex items-center gap-3 group">
                  {/* Rank */}
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: rankColor }}
                  >
                    {i + 1}
                  </span>
                  {/* Name + bar */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800 truncate pr-4">{product.key}</span>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm font-bold text-gray-900">{product.qtySold}×</span>
                        {canSeeRevenue && (
                          <span className="text-xs text-gray-400 w-16 text-right">{formatEuros(product.revenueCents)}</span>
                        )}
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barPct}%`, backgroundColor: rankColor, opacity: i < 3 ? 1 : 0.45 }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Upgrades + Customizations ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bundle Upgrades */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionTitle>Bundle Upgrades</SectionTitle>

          {orderCount === 0 ? (
            <EmptyState message="No completed orders in this period" />
          ) : totalUpgradeCount === 0 ? (
            <EmptyState message="No bundle upgrades recorded" />
          ) : (
            <>
              {/* Summary */}
              <div className="flex gap-4 mb-5 p-3 bg-gray-50 rounded-xl">
                <div className="flex-1 text-center">
                  <p className="text-2xl font-bold text-gray-900">{totalUpgradeCount}</p>
                  <p className="text-xs text-gray-400 mt-0.5">total upgrades</p>
                </div>
                {canSeeRevenue && (
                  <>
                    <div className="w-px bg-gray-200" />
                    <div className="flex-1 text-center">
                      <p className="text-2xl font-bold text-gray-900">{formatEuros(totalUpgradeRevCents)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">upgrade revenue</p>
                    </div>
                  </>
                )}
              </div>
              {/* List */}
              <div className="space-y-2.5">
                {topUpgrades.map((upgrade: any, i: number) => {
                  const barPct = (upgrade.count / maxUpgradeCount) * 100
                  return (
                    <div key={upgrade.variantName}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 truncate pr-4">{upgrade.variantName}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-bold text-gray-900">{upgrade.count}×</span>
                          {canSeeRevenue && upgrade.revenueCents > 0 && (
                            <span className="text-xs text-gray-400">{formatEuros(upgrade.revenueCents)}</span>
                          )}
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${barPct}%`, backgroundColor: getRankColor(i), opacity: i < 3 ? 1 : 0.45 }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Customization Options */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionTitle>Customization Options</SectionTitle>

          {orderCount === 0 ? (
            <EmptyState message="No completed orders in this period" />
          ) : customizationGroups.length === 0 ? (
            <EmptyState message="No customization data recorded" />
          ) : (
            <div className="space-y-5 max-h-80 overflow-y-auto pr-1">
              {customizationGroups.map((group: any) => {
                const maxCount = group.options[0]?.count ?? 1
                return (
                  <div key={group.groupName}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.groupName}</p>
                      <span className="text-xs text-gray-400">{group.totalCount}×</span>
                    </div>
                    <div className="space-y-1.5">
                      {group.options.map((opt: any, i: number) => {
                        const barPct = (opt.count / maxCount) * 100
                        return (
                          <div key={opt.label} className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs text-gray-700 truncate pr-2">{opt.label}</span>
                                <span className="text-xs font-semibold text-gray-900 flex-shrink-0">{opt.count}×</span>
                              </div>
                              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${barPct}%`,
                                    backgroundColor: i === 0 ? '#DC9C4E' : '#9CA3AF',
                                    opacity: i === 0 ? 1 : 0.6,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
      </>}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Failed to load dashboard data: {error.message}
        </div>
      )}
    </div>
  )
}

export default DashboardPage
